
import { memo } from 'react';
import { CornerDownLeft } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Avatar, Badge, StatusBadge } from '@/shared/ui';
import type { Range } from '../model/match';
import type { PaletteItem } from '../model/types';
import styles from './CommandPalette.module.css';

export function Highlight({ text, ranges }: { text: string; ranges?: Range[] }) {
  if (!ranges?.length) return <>{text}</>;
  const parts: React.ReactNode[] = [];
  let at = 0;
  for (const [start, end] of ranges) {
    if (start > at) parts.push(text.slice(at, start));
    parts.push(
      <span key={start} className={styles.hl}>
        {text.slice(start, end)}
      </span>,
    );
    at = end;
  }
  if (at < text.length) parts.push(text.slice(at));
  return <>{parts}</>;
}

export type PickMode = 'open' | 'newtab' | 'copy';

interface RowProps {
  item: PaletteItem;
  index: number;
  active: boolean;
  onHover: (index: number) => void;
  onPick: (item: PaletteItem, mode: PickMode) => void;
}

export const PaletteRow = memo(function PaletteRow({ item, index, active, onHover, onPick }: RowProps) {
  const Icon = item.icon;
  const sub = item.snippet?.length ? (
    <span className={styles.optionSub}>
      {item.snippet.map((p, i) =>
        p.mark ? (
          <mark key={i} className={styles.snippetMark}>
            {p.text}
          </mark>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </span>
  ) : item.subtitle ? (
    <span className={styles.optionSub}>{item.subtitle}</span>
  ) : null;

  return (
    <button
      id={item.id}
      type="button"
      role="option"
      aria-selected={active}
      data-active={active ? '' : undefined}
      className={cn(styles.option, !item.status?.open && item.status && styles.optionClosed)}
      tabIndex={-1}
      onMouseDown={(e) => e.preventDefault()}
      onMouseMove={() => {
        if (!active) onHover(index);
      }}
      onClick={(e) => onPick(item, e.metaKey || e.ctrlKey ? 'newtab' : e.shiftKey ? 'copy' : 'open')}
      onAuxClick={(e) => {
        if (e.button === 1) {
          e.preventDefault();
          onPick(item, 'newtab');
        }
      }}
    >
      <span className={styles.optionIcon}>
        {item.person ? (
          <Avatar name={item.label} seed={item.person.seed} className={styles.optionAvatar} />
        ) : (
          <Icon size={16} aria-hidden />
        )}
      </span>
      <span className={styles.optionMain}>
        <span className={styles.optionTitle}>
          {item.badge && <span className={styles.optionKey}>{item.badge}</span>}
          <span className={styles.optionLabel}>
            <Highlight text={item.label} ranges={item.ranges} />
          </span>
        </span>
        {sub}
      </span>
      {item.status &&
        (item.status.ticketStatus ? (
          <StatusBadge status={item.status.ticketStatus} label={item.status.label} size="sm" className={styles.optionStatus} />
        ) : (
          <Badge className={cn(styles.optionStatus, styles.optionTaskStatus)}>{item.status.label}</Badge>
        ))}
      {item.hint && <span className={styles.optionHint}>{item.hint}</span>}
      <span className={styles.optionEnter} aria-hidden>
        <CornerDownLeft size={14} />
      </span>
    </button>
  );
});
